import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { setApiBaseUrl } from '@crewcast/shared';
import ErrorBoundary, { RECOVERY_KEY } from './components/ErrorBoundary';
import { authConfig } from './config/auth';
import { setupGlobalErrorHandler } from './utils/apiErrorHandler';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthLoadingScreen from './components/AuthLoadingScreen';
import ProtectedRoute from './routes/ProtectedRoute';
import PublicRoute from './routes/PublicRoute';
import AppLayout from './layouts/AppLayout';
import BackOfficeLayout from './layouts/BackOfficeLayout';
import BackOfficeDashboardPage from './pages/backoffice/BackOfficeDashboardPage';
import BackOfficeAccountsPage from './pages/backoffice/BackOfficeAccountsPage';
import BackOfficeEngagementPage from './pages/backoffice/BackOfficeEngagementPage';
import BackOfficeKudosPage from './pages/backoffice/BackOfficeKudosPage';
import BackOfficeContentPage from './pages/backoffice/BackOfficeContentPage';
import BackOfficeRewardsPage from './pages/backoffice/BackOfficeRewardsPage';
import BackOfficeTrendsPage from './pages/backoffice/BackOfficeTrendsPage';
import LandingPage from './pages/LandingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SupportPage from './pages/SupportPage';
import BlogPage from './pages/BlogPage';
import BlogPostRouterPage from './pages/blog/BlogPostRouterPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import WelcomePage from './pages/WelcomePage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import EmployeesPage from './pages/EmployeesPage';
import EmployeeProfilePage from './pages/EmployeeProfilePage';
import RewardsPage from './pages/RewardsPage';
import RewardDetailPage from './pages/RewardDetailPage';
import ClaimedRewardsPage from './pages/ClaimedRewardsPage';
import AdminClaimedRewardsPage from './pages/AdminClaimedRewardsPage';
import ChallengesPage from './pages/ChallengesPage';
import ChallengeDetailPage from './pages/ChallengeDetailPage';
import SubmissionsPage from './pages/SubmissionsPage';
import ReviewSubmissionsPage from './pages/ReviewSubmissionsPage';
import ContentVaultPage from './pages/ContentVaultPage';
import AcceptTermsPage from './pages/AcceptTermsPage';
import './styles/global.css';

// Configure API base URL for shared package
setApiBaseUrl(authConfig.apiUrl);

// Setup global error handler for expired tokens
setupGlobalErrorHandler();

/**
 * Holds all routing until the startup auth check (token refresh) completes,
 * showing a branded loading screen instead of a blank white page.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { isLoading } = useAuth();
  if (isLoading) return <AuthLoadingScreen />;
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppRoot />
    </ErrorBoundary>
  );
}

function AppRoot() {
  // Once the app renders and stays up, any earlier crash has been recovered —
  // reset the staged-recovery counter so a future, unrelated crash starts fresh.
  useEffect(() => {
    const t = setTimeout(() => sessionStorage.removeItem(RECOVERY_KEY), 5000);
    return () => clearTimeout(t);
  }, []);

  return (
    <AuthProvider>
    <AuthGate>
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Public Landing Pages */}
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/contact" element={<SupportPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        {/* Backwards compatible */}
        <Route path="/support" element={<Navigate to="/contact" replace />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostRouterPage />} />

        {/* Public Auth Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/welcome"
          element={
            <PublicRoute>
              <WelcomePage />
            </PublicRoute>
          }
        />

        {/* Accept Terms - shown during onboarding */}
        <Route
          path="/accept-terms"
          element={
            <ProtectedRoute>
              <AcceptTermsPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProfilePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute>
              <AppLayout>
                <EmployeesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:userId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <EmployeeProfilePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/rewards"
          element={
            <ProtectedRoute requirePointsEnabled>
              <AppLayout>
                <RewardsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rewards/:rewardId"
          element={
            <ProtectedRoute requirePointsEnabled>
              <AppLayout>
                <RewardDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/claimed-rewards"
          element={
            <ProtectedRoute requirePointsEnabled>
              <AppLayout>
                <ClaimedRewardsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/claimed-rewards"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AdminClaimedRewardsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/moments"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ChallengesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/moments/:challengeId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ChallengeDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/submissions"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SubmissionsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/review-submissions"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ReviewSubmissionsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/content-vault"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ContentVaultPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Back Office (superadmin only) */}
        <Route
          path="/backoffice"
          element={
            <ProtectedRoute requireRole="superadmin">
              <BackOfficeLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<BackOfficeDashboardPage />} />
          <Route path="accounts" element={<BackOfficeAccountsPage />} />
          <Route path="engagement" element={<BackOfficeEngagementPage />} />
          <Route path="kudos" element={<BackOfficeKudosPage />} />
          <Route path="content" element={<BackOfficeContentPage />} />
          <Route path="rewards" element={<BackOfficeRewardsPage />} />
          <Route path="trends" element={<BackOfficeTrendsPage />} />
        </Route>

        {/* 404 - redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </AuthGate>
    </AuthProvider>
  );
}
